import React, { FC, useCallback, useMemo, useEffect } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'components/Dropdown/Dropdown';
import { DropdownItem } from 'components/DropdownItem/DropdownItem';
import { ServicesDropdown } from 'components/ServicesDropdown';
import { useServiceType } from 'hooks/context/serviceType';
import { useCart } from 'hooks/context/cart';
import { useGiftCardCart } from 'hooks/context/giftCardCart';
import { useMerchant } from 'hooks/context/merchant';
import { useMerchantState } from 'hooks/context/MerchantState';
import { useDelivery } from 'hooks/context/Delivery';
import { useScheduledOrderDisplay } from 'hooks/context/ScheduledOrderDisplay';
import { useSmartNavigate } from 'hooks/useSmartNavigate';
import { useCartPreview } from 'hooks/context/CartPreview';
import { CART_TEST_IDS } from './Cart.consts';
import { ServiceType, ServiceTypeLabel } from 'hooks/context/serviceType/models';
import { useDeliveryValidation } from 'hooks/useDeliveryValidation';
import { useGetLatestAddress } from 'hooks/useApi/useAddress';
import {
  isForWhenReadOnly,
  isOrderTypeReadOnly,
  noDeliveryAddress,
  hasDateOrTime,
  hasDateAndTime,
  canScheduleOrder,
} from './CartServices.util';

import styles from './CartServices.module.scss';

export const CartServices: FC<{ onClick?: () => void }> = ({ onClick, ...props }) => {
  const { t } = useTranslation();
  const navigate = useSmartNavigate();
  const { closeCartPreview } = useCartPreview();
  const { update, currentServiceType, isDelivery, serviceTypes } = useServiceType();
  const { itemCount: cartItemCount } = useCart();
  const { itemCount: giftCardCount } = useGiftCardCart();
  const { merchant } = useMerchant();
  const { isAcceptingOrders, showScheduledOrderClosedState, showScheduledOrderState } = useMerchantState();
  const { deliveryAddress, deliveryAsapTime, defaultAddressHasBeenLoaded } = useDelivery();
  const { asyncValidateAndSetDefaultAddress } = useDeliveryValidation();
  const {
    currentDate,
    currentTime,
    displayValueDate,
    displayValueTime,
    resetScheduledOrderToInitialValues,
    resetScheduledOrderState,
  } = useScheduledOrderDisplay();

  const itemCount = cartItemCount + giftCardCount;

  useEffect(() => {
    if (!hasDateAndTime(displayValueDate, displayValueTime)) {
      showScheduledOrderClosedState ? resetScheduledOrderToInitialValues() : resetScheduledOrderState();
    }
  }, [
    displayValueDate,
    displayValueTime,
    resetScheduledOrderState,
    resetScheduledOrderToInitialValues,
    showScheduledOrderClosedState,
  ]);

  const scheduledTimes = useMemo(() => {
    const asapTime = isDelivery
      ? { label: deliveryAsapTime }
      : { label: t('cart.pickUpAsap', { count: merchant?.leadTime }) };

    return [asapTime];
  }, [deliveryAsapTime, isDelivery, merchant?.leadTime, t]);

  const currentScheduledOrder = hasDateAndTime(currentDate, currentTime)
    ? t('scheduledOrder.currentScheduledOrder', { currentDate: currentDate?.label, currentTime: currentTime?.label })
    : scheduledTimes[0].label;

  const serverDefaultAddress = useGetLatestAddress();
  const defaultAddress = serverDefaultAddress.data;

  const handleServiceType = useCallback((serviceType: ServiceTypeLabel) => {
    if (noDeliveryAddress(serviceType.name, deliveryAddress)) {
      if (defaultAddress && !defaultAddressHasBeenLoaded){
        asyncValidateAndSetDefaultAddress(defaultAddress);
      } else {
        closeCartPreview();
        navigate('~address');
      }
    } else {
      if (serviceType.name === ServiceType.DELIVERY) {
        resetScheduledOrderState();
      }
      update(serviceType.name);
      if (!hasDateOrTime(currentDate, currentTime)) resetScheduledOrderState();
    }
  }, [closeCartPreview, currentDate, currentTime, defaultAddress, defaultAddressHasBeenLoaded, deliveryAddress, navigate, resetScheduledOrderState, update, asyncValidateAndSetDefaultAddress]);

  const handleSelect = (selectedService: ServiceTypeLabel) => {
    handleServiceType(selectedService);
  };

  return (
    <>
      <ServicesDropdown
        currentServiceType={currentServiceType}
        handleSelect={handleSelect}
        readonly={isOrderTypeReadOnly(serviceTypes, itemCount)}
      />
      <Dropdown
        readonly={isForWhenReadOnly(showScheduledOrderState, itemCount)}
        label={t('cart.forWhen')}
        defaultValue={currentScheduledOrder}
        dataTestId={CART_TEST_IDS.pickupTimeDropDownBtn}
        selectClassName={styles.scheduledOrderDropdownSelect}
      >
        {canScheduleOrder(showScheduledOrderClosedState, isAcceptingOrders) &&
          scheduledTimes.map((item, index) => (
            <DropdownItem
              className={styles.item}
              key={index}
              value={item.label}
              data-testid={item.label}
              onClick={() => resetScheduledOrderState()}
            >
              <div className={clsx(styles.name)}>{item.label}</div>
            </DropdownItem>
          ))}
        <button className={styles.item} data-testid={CART_TEST_IDS.scheduledOrderDropDownBtn} onClick={onClick}>
          <div className={clsx(styles.name)}>{t('scheduledOrder.button')}</div>
        </button>
      </Dropdown>
    </>
  );
};
